import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import SectionHeading from '../components/ui/SectionHeading';
import MetricsSection from '../components/ui/MetricsSection';
import TestimonialCard from '../components/ui/TestimonialCard';
import BookSection from '../components/ui/BookSection';
import FeedbackLoop from '../components/ui/FeedbackLoop';
import ConnectDotsBackground from '../components/ui/ConnectDotsBackground';
import { personalInfo, caseStudies, testimonials } from '../data/content';

const Home = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-brand-dark transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-white dark:bg-brand-dark">
        <div 
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none"
          style={{ 
            backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', 
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
            WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
          }}
        ></div>

        <ConnectDotsBackground />

        <div className="max-w-6xl mx-auto px-6 relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="shrink-0 relative mx-auto"
          >
            <div className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-96 md:h-96 z-20 shrink-0 mx-auto">
              <img 
                src="/Julie_Adams_MSDM.png"
                alt={personalInfo.name} 
                className="w-full h-full object-cover rounded-full shadow-2xl relative z-10 border-8 border-white dark:border-brand-dark"
              />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center lg:text-left"
          >
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-heading font-extrabold text-slate-900 dark:text-white mb-6 leading-tight">
              I Diagnosed The <br className="hidden lg:block"/> <span className="text-brand-red dark:text-brand-yellow">$80K Tool Trap™</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-slate-700 dark:text-slate-100 mb-4 leading-snug">
              Now I Help Marketing Leaders <br />
              <span className="text-brand-red dark:text-brand-yellow">Escape Tool Chaos</span> <span className="text-slate-900 dark:text-white">& Build Strategic Clarity</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 max-w-xl font-medium mx-auto lg:mx-0">
              Bridging the gaps between Marketing Strategy, Marketing Operations, and MarTech Infrastructure that connects customer data, automation tools, and analytics to run successful campaigns.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4">
              <Link to="/work-with-me" className="px-8 py-4 bg-brand-cyan dark:bg-brand-red text-brand-dark dark:text-white text-sm uppercase font-bold tracking-widest hover:bg-brand-yellow hover:text-brand-dark transition-all duration-300 w-full sm:w-auto rounded flex items-center justify-center gap-2 shadow-lg shadow-brand-cyan/20 dark:shadow-brand-red/20 text-center">
               <SafeIcon name="Briefcase" className="w-4 h-4" /> Work With Me
              </Link>
              <Link to="/fractional-cmo" className="px-8 py-4 bg-brand-yellow dark:bg-brand-cyan text-brand-dark border-2 border-brand-yellow dark:border-brand-cyan text-sm uppercase font-bold tracking-widest hover:opacity-90 transition-all duration-300 w-full sm:w-auto rounded flex items-center justify-center gap-2 shadow-lg">
                <SafeIcon name="Search" className="w-4 h-4" /> My Advisory Services
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 max-w-xl mx-auto lg:mx-0 font-medium">
              See how I provide transparent ROI tracking, unified data schemas, and strategic foresight directly in a <Link to="/portal">secure executive dashboard.</Link>
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white dark:bg-brand-darker">
        <div className="max-w-7xl mx-auto px-6">
          <MetricsSection />
        </div>
      </section>

      <BookSection />

      <section className="py-24 bg-white dark:bg-brand-darker">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading title="Strategic Engagements" subtitle="New Release" align="center" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {caseStudies.map((study) => (
              <Link to={`/work/${study.id}`} key={study.id} className="group bg-slate-50 dark:bg-brand-dark rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 block border border-slate-100 dark:border-slate-800 relative">
                {study.isNew && (
                  <span className="absolute top-4 right-4 bg-brand-red text-white text-[9px] uppercase tracking-widest px-3 py-1 font-bold rounded-full z-20 shadow-lg">New Release</span>
                )}
                <div className="aspect-video overflow-hidden relative">
                  <div className="absolute inset-0 bg-brand-cyan/20 opacity-0 group-hover:opacity-100 transition-opacity z-10 mix-blend-multiply"></div>
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover filter grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                </div>
                <div className="p-8">
                  <span className="text-[10px] uppercase tracking-widest text-brand-yellow font-black mb-3 block">{study.category}</span>
                  <h3 className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-4 group-hover:text-brand-cyan transition-colors">{study.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 font-medium">{study.summary}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/work" className="inline-flex items-center gap-2 text-sm uppercase font-bold tracking-widest text-brand-dark dark:text-white hover:text-brand-red dark:hover:text-brand-cyan transition-colors">
              Explore Full Portfolio <SafeIcon name="ArrowRight" className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <FeedbackLoop />

      <section className="py-24 bg-brand-dark dark:bg-brand-darker overflow-hidden relative">
        <div 
          className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none" 
          style={{ 
            backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', 
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
            WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="block text-xs uppercase tracking-[0.2em] text-brand-yellow mb-3 font-bold">High-Profile Impact</span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">Client Perspectives</h2>
            <div className="w-16 h-1.5 bg-brand-cyan mx-auto mt-6"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {testimonials.map((t, i) => (
              <TestimonialCard key={i} testimonial={t} index={i} />
            ))}
          </div>

          <div className="mt-12">
            <div className="elfsight-app-d6cc8448-5129-49ad-af6b-bc0907fd884a" data-elfsight-app-lazy></div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default Home;

